import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import { NotConfiguredError, isConfigured } from "@/lib/supabase";
import { formatParisStamp } from "@shared/nba-time";
import type { Reliability } from "@/lib/types";
import { Badge, SkeletonRows, cx } from "./primitives";

type EmptyKind = "no-data" | "not-written" | "source-limit" | "not-configured" | "error";

const EMPTY_COPY: Record<EmptyKind, { kicker: string; title: string; body: string }> = {
  "no-data": {
    kicker: "Aucune donnée synchronisée",
    title: "Rien à afficher pour l'instant",
    body: "Cette vue se remplira après une synchronisation depuis l'administration. Aucune donnée n'est simulée.",
  },
  "not-written": {
    kicker: "Contenu éditorial",
    title: "Section non encore rédigée",
    body: "Ce contenu est rédigé et sourcé par la rédaction. Tant qu'il n'est pas publié, rien n'est affiché à sa place.",
  },
  "source-limit": {
    kicker: "Limite de la source",
    title: "Donnée non fournie par la source actuelle",
    body: "Le fournisseur de données utilisé ne publie pas cette information. Elle n'est pas estimée.",
  },
  "not-configured": {
    kicker: "Configuration requise",
    title: "Base de données non connectée",
    body: "Renseignez VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY, appliquez les migrations, puis lancez une première synchronisation.",
  },
  error: { kicker: "Erreur", title: "Chargement impossible", body: "La requête a échoué. Réessayez dans un instant." },
};

export function EmptyState({ kind = "no-data", title, children, action, compact }: { kind?: EmptyKind; title?: ReactNode; children?: ReactNode; action?: ReactNode; compact?: boolean }) {
  const c = EMPTY_COPY[kind];
  return (
    <div className={cx("flex flex-col items-start gap-2 rounded-xl border border-dashed border-line-strong", compact ? "p-4" : "p-6 sm:p-8")}>
      <span className={cx("kicker", kind === "error" ? "text-neg" : kind === "not-configured" ? "text-warn" : "")}>{c.kicker}</span>
      <p className={cx("font-semibold text-ink", compact ? "text-base" : "text-lg")}>{title ?? c.title}</p>
      <div className="max-w-2xl text-sm text-muted">{children ?? c.body}</div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Rend l'état d'une requête : chargement (squelette), base non configurée, erreur, vide, ou contenu.
 */
export function QueryView<T>({ query, children, empty, skeleton, isEmpty }: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  empty?: ReactNode;
  skeleton?: ReactNode;
  isEmpty?: (data: T) => boolean;
}) {
  if (!isConfigured || query.error instanceof NotConfiguredError) return <EmptyState kind="not-configured" />;
  if (query.isPending) return <>{skeleton ?? <SkeletonRows />}</>;
  if (query.isError) {
    return <EmptyState kind="error">{(query.error as Error)?.message}</EmptyState>;
  }
  const data = query.data as T;
  const emptyCheck = isEmpty ?? ((d: T) => d === null || d === undefined || (Array.isArray(d) && d.length === 0));
  if (emptyCheck(data)) return <>{empty ?? <EmptyState kind="no-data" />}</>;
  return <>{children(data)}</>;
}

const RELIABILITY: Record<Reliability, { label: string; tone: "pos" | "d1" | "d3" | "d4" | "warn" | "neutral"; help: string }> = {
  verifie: { label: "Vérifié", tone: "pos", help: "Donnée confirmée par une source primaire ou recoupée par la rédaction." },
  api: { label: "Source API", tone: "d4", help: "Donnée importée telle quelle depuis le fournisseur de données." },
  derive: { label: "Calculé", tone: "d1", help: "Valeur calculée par la plateforme à partir des données importées (méthode sur /methodologie)." },
  editorial: { label: "Analyse", tone: "d3", help: "Analyse ou interprétation éditoriale signée." },
  non_verifie: { label: "Non vérifié", tone: "warn", help: "Information saisie mais pas encore vérifiée." },
  conteste: { label: "Contesté", tone: "warn", help: "Information disputée, présentée comme telle." },
};

export function ProvenanceBadge({ kind, className }: { kind: Reliability; className?: string }) {
  const r = RELIABILITY[kind];
  return (
    <Badge tone={r.tone} className={className} title={r.help}>
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {r.label}
    </Badge>
  );
}

/** Ligne de traçabilité : source + date de collecte / calcul */
export function Provenance({ kind, source, at, method, className }: { kind: Reliability; source?: ReactNode; at?: string | null; method?: ReactNode; className?: string }) {
  return (
    <div className={cx("flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-faint", className)}>
      <ProvenanceBadge kind={kind} />
      {source && <span>Source : {source}</span>}
      {at && <span>Mis à jour le {formatParisStamp(at)}</span>}
      {method && <span>Méthode : {method}</span>}
    </div>
  );
}

export function SourceLink({ name, url }: { name: string; url?: string | null }) {
  if (!url) return <span>{name}</span>;
  if (url.startsWith("/")) return <Link className="underline underline-offset-2 hover:text-accent-text" to={url}>{name}</Link>;
  return <a className="underline underline-offset-2 hover:text-accent-text" href={url} target="_blank" rel="noreferrer noopener">{name}</a>;
}

export function NotConfiguredBanner() {
  if (isConfigured) return null;
  return (
    <div role="status" className="border-b border-warn/30 bg-warn/10 px-4 py-2 text-center text-sm text-warn">
      Mode démonstration de l'interface : aucune base n'est connectée, aucune donnée n'est affichée.{" "}
      <Link to="/methodologie#installation" className="underline underline-offset-2">Guide d'installation</Link>
    </div>
  );
}
